// src/components/F8EvidenceReviewModal.js
// AÇÃO 4 F8: Painel de revisão de evidências IA pendentes.
// Lista cards com entity/field/sugestão/reasoning; permite aprovar, rejeitar ou adiar.

import React, { useMemo, useState } from 'react';
import {
  X, CheckCircle2, XCircle, AlertCircle, Brain, ExternalLink,
  MessageSquare, RefreshCw, Mail, ArrowRight,
} from 'lucide-react';
import { useEvidences } from '../hooks/useEvidences';
import { supabase } from '../supabaseClient';
import useBulkSelection from './shared/useBulkSelection';
import BulkActionBar from './shared/BulkActionBar';
import SelectAllCheckbox from './shared/SelectAllCheckbox';
import FilterChips from './shared/FilterChips';

const ALL_FILTER = '__all';

// Fluxo do bulk:
//   Filtra por entity_type → SelectAll → BulkActionBar
//   Aprovar: heterogeneity check (se >1 entity_type OU >1 field_name selecionados, 2º confirm)
//   Rejeitar: window.prompt UMA razão genérica → aplica a todos
//   Loop sequencial em supabase.functions.invoke('apply-evidence') — NÃO altera EF

// ---------- helpers ----------
const renderValue = (val) => {
  if (val === null || val === undefined) return <span className="text-slate-400 italic">—</span>;
  if (typeof val === 'object') {
    return (
      <pre className="text-[11px] font-mono text-slate-700 whitespace-pre-wrap break-words">
        {JSON.stringify(val, null, 2)}
      </pre>
    );
  }
  return <span className="text-sm text-slate-800 break-words">{String(val)}</span>;
};

function asUuid(v) {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && typeof v.value === 'string') return v.value;
  return null;
}

function fmtDateShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function OfertaLabel({ oferta, fallbackUuid }) {
  if (!oferta) {
    return (
      <span className="text-xs text-slate-600 font-mono break-all">
        {fallbackUuid}{' '}
        <span className="text-[10px] text-red-600 font-sans">(oferta não encontrada)</span>
      </span>
    );
  }
  const emp = oferta.empresas?.company_name || oferta.empresas?.empresa || '?';
  return (
    <span className="text-sm">
      <span className="font-semibold text-slate-800">{emp}</span>
      <span className="text-slate-400 mx-1">×</span>
      <span className="text-slate-800">{oferta.nome || '(sem nome)'}</span>
      {oferta.status && (
        <span className="ml-2 text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
          {oferta.status}
        </span>
      )}
    </span>
  );
}

const ConfidenceBar = ({ value }) => {
  const pct = Math.max(0, Math.min(1, Number(value) || 0)) * 100;
  const color =
    pct >= 80 ? 'bg-emerald-500' :
    pct >= 60 ? 'bg-amber-500' :
    'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-600 font-mono">{pct.toFixed(0)}%</span>
    </div>
  );
};

// ---------- card de evidência ----------
const EvidenceCard = ({
  ev, onApprove, onReject, onPostpone, disabled, selected, onToggleSelect,
  emailMap = {}, ofertaMap = {},
}) => {
  const [overrideValue, setOverrideValue] = useState(null);

  const nomeEmpresa = ev.empresa?.empresa || ev.empresa?.company_name || ev.entity_id;
  const options = Array.isArray(ev.metadata?.options) ? ev.metadata.options : [];
  const appliedValue = overrideValue !== null ? overrideValue : ev.suggested_value;

  // Resolução humanizada para o caso oferta_id em email
  const isOfertaEmailLink = ev.entity_type === 'email' && ev.field_name === 'oferta_id';
  const emailObj = isOfertaEmailLink ? emailMap[ev.entity_id] : null;
  const suggestedOfertaId = isOfertaEmailLink ? asUuid(ev.suggested_value) : null;
  const previousOfertaId  = isOfertaEmailLink ? asUuid(ev.previous_value) : null;
  const suggestedOferta = suggestedOfertaId ? ofertaMap[suggestedOfertaId] : null;
  const previousOferta  = previousOfertaId  ? ofertaMap[previousOfertaId]  : null;

  return (
    <li
      className={`bg-white border rounded-lg p-4 space-y-3 transition-colors ${
        selected ? 'border-emerald-400 bg-emerald-50/40' : 'border-slate-200'
      }`}
    >
      {/* Header do card */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1 flex items-start gap-3">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelect}
            aria-label="Selecionar evidência"
            className="cursor-pointer w-5 h-5 mt-0.5 accent-emerald-600 flex-shrink-0 rounded"
          />
          <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {isOfertaEmailLink ? (
              <span className="font-bold text-slate-800 text-sm">Vincular email à oferta</span>
            ) : (
              <>
                <span className="font-bold text-slate-800 text-sm truncate">{nomeEmpresa}</span>
                <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                  {ev.entity_type}
                </span>
              </>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
            {!isOfertaEmailLink && (
              <>
                <span>campo: <code className="text-slate-700 font-mono">{ev.field_name}</code></span>
                <span className="text-slate-300">·</span>
              </>
            )}
            <span className="flex items-center gap-1">
              <Brain size={10} className="text-indigo-500" />
              <span className="font-mono">{ev.agent}</span>
            </span>
          </div>
          </div>
        </div>
        <ConfidenceBar value={ev.confidence} />
      </div>

      {isOfertaEmailLink ? (
        /* Render humanizado: email → oferta */
        <div className="space-y-2">
          {/* Email source */}
          <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
            <div className="flex items-start gap-2">
              <Mail size={12} className="text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                {emailObj ? (
                  <>
                    <div className="font-medium text-slate-800 truncate" title={emailObj.subject || ''}>
                      {emailObj.subject || '(sem assunto)'}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {emailObj.from_email || '(sem remetente)'}
                      {emailObj.data_envio && <span className="mx-1 text-slate-400">·</span>}
                      {emailObj.data_envio && <span>{fmtDateShort(emailObj.data_envio)}</span>}
                    </div>
                  </>
                ) : (
                  <span className="text-slate-600 font-mono break-all">
                    {ev.entity_id}{' '}
                    <span className="text-[10px] text-red-600 font-sans">(email não encontrado)</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Estado atual → sugestão */}
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">Atual:</span>
              {previousOfertaId ? (
                <OfertaLabel oferta={previousOferta} fallbackUuid={previousOfertaId} />
              ) : (
                <span className="text-slate-500 italic">Sem oferta vinculada</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <ArrowRight size={11} className="text-emerald-600 flex-shrink-0" />
              <span className="text-[10px] uppercase tracking-wide text-emerald-700">Sugestão:</span>
              {suggestedOfertaId ? (
                <OfertaLabel oferta={suggestedOferta} fallbackUuid={suggestedOfertaId} />
              ) : (
                <span className="text-slate-500 italic">—</span>
              )}
            </div>
          </div>

          <div className="text-[11px] text-slate-500 italic px-1">
            Se aprovado: este email será vinculado à oferta acima.
          </div>
        </div>
      ) : (
        /* Render padrão (outros field_names) */
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-slate-50 border border-slate-200 rounded p-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Valor atual</div>
            {renderValue(ev.previous_value)}
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 mb-1">Sugestão IA</div>
            {renderValue(appliedValue)}
          </div>
        </div>
      )}

      {/* Detalhes técnicos (UUIDs) — só pra isOfertaEmailLink */}
      {isOfertaEmailLink && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-400 hover:text-slate-600 text-[11px]">
            Detalhes técnicos
          </summary>
          <div className="mt-1 bg-slate-50 border border-slate-200 rounded p-2 text-[10px] font-mono text-slate-600 space-y-0.5">
            <div>field_name: oferta_id</div>
            <div>entity: email · {ev.entity_id}</div>
            {previousOfertaId && <div>previous_value: {previousOfertaId}</div>}
            {suggestedOfertaId && <div>suggested_value: {suggestedOfertaId}</div>}
          </div>
        </details>
      )}

      {/* Reasoning */}
      {ev.reasoning && (
        <div className="bg-slate-50 border-l-2 border-indigo-300 p-2 text-xs text-slate-600 italic">
          {ev.reasoning}
        </div>
      )}

      {/* Alternativas (metadata.options) */}
      {options.length > 0 && (
        <div>
          <div className="text-[11px] text-slate-600 mb-1">Alternativas propostas pelo agente:</div>
          <div className="flex flex-wrap gap-1">
            {options.map((opt, i) => {
              const optStr = typeof opt === 'object' ? JSON.stringify(opt) : String(opt);
              const isSelected =
                JSON.stringify(appliedValue) === JSON.stringify(
                  typeof opt === 'object' && opt.value !== undefined ? opt.value : opt
                );
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled}
                  onClick={() => setOverrideValue(
                    typeof opt === 'object' && opt.value !== undefined ? opt.value : opt
                  )}
                  className={`text-[11px] px-2 py-0.5 rounded border font-mono truncate max-w-xs ${
                    isSelected
                      ? 'bg-emerald-100 border-emerald-400 text-emerald-800'
                      : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                  title={optStr}
                >
                  {typeof opt === 'object' && opt.label ? opt.label : optStr}
                </button>
              );
            })}
            {overrideValue !== null && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => setOverrideValue(null)}
                className="text-[11px] px-2 py-0.5 rounded border border-slate-300 text-slate-500 hover:bg-slate-100"
              >
                resetar
              </button>
            )}
          </div>
        </div>
      )}

      {/* Metadata extra (se existir e não for só options) */}
      {ev.metadata && Object.keys(ev.metadata).filter(k => k !== 'options').length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
            ver metadata completa
          </summary>
          <pre className="mt-1 bg-slate-50 border border-slate-200 rounded p-2 text-[11px] font-mono text-slate-700 whitespace-pre-wrap break-words">
            {JSON.stringify(ev.metadata, null, 2)}
          </pre>
        </details>
      )}

      {/* Ações */}
      <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-100">
        <button
          onClick={() => onApprove(ev, overrideValue)}
          disabled={disabled}
          className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1 font-semibold"
          title="Aprovar e aplicar"
        >
          <CheckCircle2 size={12} /> Aprovar
        </button>
        <button
          onClick={() => onReject(ev)}
          disabled={disabled}
          className="text-xs px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
          title="Rejeitar (pede razão)"
        >
          <XCircle size={12} /> Rejeitar
        </button>
        <button
          onClick={() => onPostpone(ev)}
          disabled={disabled}
          className="text-xs px-3 py-1.5 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 disabled:opacity-50 flex items-center gap-1"
          title="Manter pendente, decidir depois"
        >
          <MessageSquare size={12} /> Discutir depois
        </button>
        <span className="text-[10px] text-slate-400 ml-auto font-mono truncate" title={ev.id}>
          evidence {ev.id.slice(0, 8)}… · {new Date(ev.created_at).toLocaleDateString('pt-BR')}
        </span>
      </div>
    </li>
  );
};

// ---------- modal ----------
const F8EvidenceReviewModal = ({ onClose, userEmail }) => {
  const { evidences, pendingCount, emailMap, ofertaMap, loading, error, loadPending, decide } = useEvidences();
  const [feedback, setFeedback] = useState(null); // {tipo: 'sucesso'|'erro', texto}
  const [entityFilter, setEntityFilter] = useState(ALL_FILTER);
  const sel = useBulkSelection();

  const decidedBy = userEmail || 'humano:desconhecido';

  // Chips por entity_type (auto-hide se ≤1 tipo)
  const { filterChips, filteredEvidences } = useMemo(() => {
    const counts = new Map();
    for (const e of evidences) {
      const t = e.entity_type || 'desconhecido';
      counts.set(t, (counts.get(t) || 0) + 1);
    }
    const chips = [
      { key: ALL_FILTER, label: 'Todos', count: evidences.length },
      ...Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([t, n]) => ({ key: t, label: t, count: n })),
    ];
    const filtered = entityFilter === ALL_FILTER
      ? evidences
      : evidences.filter((e) => (e.entity_type || 'desconhecido') === entityFilter);
    return { filterChips: chips, filteredEvidences: filtered };
  }, [evidences, entityFilter]);

  const handleFilterChange = (key) => {
    setEntityFilter(key);
    sel.deselectAll();
  };

  const handleApprove = async (ev, overrideValue) => {
    const result = await decide({
      evidence_id: ev.id,
      decision: 'approved',
      decided_by: decidedBy,
      decision_reason: 'aprovado via painel F8',
      override_value: overrideValue !== null ? overrideValue : undefined,
    });
    if (result) {
      setFeedback({ tipo: 'sucesso', texto: `✅ Aprovado: ${ev.field_name} atualizado.` });
    } else {
      setFeedback({ tipo: 'erro', texto: `Erro ao aprovar: verifique o console.` });
    }
  };

  const handleReject = async (ev) => {
    const reason = window.prompt(
      `Razão para rejeitar a sugestão da IA (${ev.field_name})?`,
      ''
    );
    if (!reason || !reason.trim()) return;
    const result = await decide({
      evidence_id: ev.id,
      decision: 'rejected',
      decided_by: decidedBy,
      decision_reason: reason.trim(),
    });
    if (result) {
      setFeedback({ tipo: 'sucesso', texto: `❌ Rejeitado: ${ev.field_name}.` });
    } else {
      setFeedback({ tipo: 'erro', texto: `Erro ao rejeitar: verifique o console.` });
    }
  };

  const handlePostpone = () => {
    setFeedback({ tipo: 'sucesso', texto: 'Mantido pendente — decisão adiada.' });
  };

  // --- Bulk via Edge Function apply-evidence (chamada direta, evita re-load por item) ---
  // Em erro no meio do loop: para, reload + mantém seleção dos restantes (igual Fila Empresas).
  const invokeApplyEvidence = async (body) => {
    const { data, error: fnErr } = await supabase.functions.invoke('apply-evidence', { body });
    if (fnErr) throw fnErr;
    if (data?.error || data?.ok === false) {
      throw new Error(data?.error || 'Falha ao aplicar decisão');
    }
    return data;
  };

  const bulkApply = async (decision, decisionReason) => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;
    let ok = 0;
    let failedAt = null;

    for (const id of ids) {
      try {
        await invokeApplyEvidence({
          evidence_id: id,
          decision,
          decided_by: decidedBy,
          ...(decisionReason ? { decision_reason: decisionReason } : {}),
        });
        ok += 1;
      } catch (err) {
        failedAt = { id, msg: err.message || String(err) };
        break;
      }
    }

    await loadPending(); // ressincroniza

    if (failedAt) {
      const processed = new Set(ids.slice(0, ok));
      sel.deselectAll();
      const remaining = ids.filter((x) => !processed.has(x));
      if (remaining.length > 0) sel.selectAll(remaining);
      setFeedback({
        tipo: 'erro',
        texto: `Erro após ${ok} sucesso(s): ${failedAt.msg}`,
      });
      throw new Error(failedAt.msg); // BulkActionBar mantém UI sem onClear
    }

    const verb = decision === 'approved' ? 'aprovada' : 'rejeitada';
    setFeedback({
      tipo: 'sucesso',
      texto: `✅ ${ok} ${ok === 1 ? `sugestão ${verb}` : `sugestões ${verb}s`}`,
    });
  };

  const bulkApprove = async () => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;
    const selectedEvs = evidences.filter((e) => sel.isSelected(e.id));
    const types = new Set(selectedEvs.map((e) => e.entity_type));
    const fields = new Set(selectedEvs.map((e) => e.field_name));
    if (types.size > 1 || fields.size > 1) {
      const ok = window.confirm(
        'Você está aprovando sugestões de tipos/campos diferentes. ' +
        'Isso pode alterar várias entidades distintas. Tem certeza?',
      );
      if (!ok) throw new Error('Aprovação heterogênea cancelada');
    }
    await bulkApply('approved', 'aprovado em lote via painel F8');
  };

  const bulkReject = async () => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;
    const reason = window.prompt('Motivo da rejeição em lote (opcional):', 'rejeitado em lote via painel F8');
    if (reason === null) throw new Error('Rejeição cancelada'); // user cancelou prompt
    await bulkApply('rejected', reason || 'rejeitado em lote via painel F8');
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center pt-8 px-4">
      <div className="bg-slate-50 rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white rounded-t-lg">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Governança IA</div>
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              🔍 Painel F8 — Revisão de Sugestões IA
              {pendingCount > 0 && (
                <span className="text-xs font-normal bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                  {pendingCount} pendente{pendingCount === 1 ? '' : 's'}
                </span>
              )}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Revise, aprove ou rejeite sugestões geradas por agentes IA.
              Aprovações aplicam o valor sugerido na entidade afetada.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {filteredEvidences.length > 0 && (
              <SelectAllCheckbox
                totalCount={filteredEvidences.length}
                selectedCount={sel.count}
                onSelectAll={() => sel.selectAll(filteredEvidences.map((e) => e.id))}
                onDeselectAll={sel.deselectAll}
              />
            )}
            <button
              onClick={loadPending}
              disabled={loading}
              className="text-slate-400 hover:text-slate-600 p-1"
              title="Recarregar"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Feedback */}
        {feedback && (
          <div className={`px-4 py-2 text-sm flex items-center justify-between ${
            feedback.tipo === 'sucesso'
              ? 'bg-emerald-50 text-emerald-800 border-b border-emerald-200'
              : 'bg-red-50 text-red-800 border-b border-red-200'
          }`}>
            <span>{feedback.texto}</span>
            <button
              onClick={() => setFeedback(null)}
              className="text-slate-400 hover:text-slate-600 text-xs"
            >
              fechar
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {evidences.length > 0 && (
            <FilterChips
              chips={filterChips}
              activeKey={entityFilter}
              onChange={handleFilterChange}
            />
          )}

          {loading && evidences.length === 0 && (
            <div className="text-sm text-slate-500 py-8 text-center">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
              Carregando evidências pendentes...
            </div>
          )}

          {!loading && error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>Erro: {error}</span>
            </div>
          )}

          {!loading && !error && evidences.length === 0 && (
            <div className="text-sm text-slate-500 italic py-10 text-center bg-white rounded border border-slate-200">
              <CheckCircle2 size={32} className="text-emerald-500 mx-auto mb-2" />
              Tudo em dia! Nenhuma sugestão aguardando revisão.
            </div>
          )}

          {!loading && !error && evidences.length > 0 && filteredEvidences.length === 0 && (
            <div className="bg-white border border-slate-200 rounded p-6 text-center">
              <p className="text-xs text-slate-500">
                Nenhuma sugestão neste filtro.{' '}
                <button
                  onClick={() => handleFilterChange(ALL_FILTER)}
                  className="text-blue-600 hover:underline"
                >
                  Limpar filtro
                </button>
              </p>
            </div>
          )}

          {filteredEvidences.length > 0 && (
            <ul className="space-y-3">
              {filteredEvidences.map(ev => (
                <EvidenceCard
                  key={ev.id}
                  ev={ev}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onPostpone={handlePostpone}
                  disabled={loading}
                  selected={sel.isSelected(ev.id)}
                  onToggleSelect={() => sel.toggle(ev.id)}
                  emailMap={emailMap}
                  ofertaMap={ofertaMap}
                />
              ))}
            </ul>
          )}
        </div>

        <BulkActionBar
          count={sel.count}
          onClear={sel.deselectAll}
          actions={[
            {
              label: 'Aprovar selecionados',
              icon: CheckCircle2,
              color: 'green',
              confirmMessage: (n) =>
                `Aprovar ${n} sugest${n === 1 ? 'ão' : 'ões'} IA? Verifique se o filtro está correto.`,
              onClick: bulkApprove,
            },
            {
              label: 'Rejeitar selecionados',
              icon: XCircle,
              color: 'red',
              confirmMessage: (n) =>
                `Rejeitar ${n} sugest${n === 1 ? 'ão' : 'ões'}?`,
              onClick: bulkReject,
            },
          ]}
        />

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-white rounded-b-lg text-xs text-slate-500 flex items-center justify-between">
          <span>
            decidindo como <span className="font-mono text-slate-700">{decidedBy}</span>
          </span>
          <a
            href="https://supabase.com/dashboard/project/srslyudbtygfswkwzivg/editor"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-slate-700"
          >
            Auditoria SQL <ExternalLink size={10} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default F8EvidenceReviewModal;
